import { Component, createRef } from 'preact';
import {
  TEXT_MUTED,
  GOLD, PANEL_BG, PARCHMENT, ACCENT_GOLD,
  CONTENT_BG_DEFAULT,
  MedievalBtn,
} from '../../lib/medievalTheme';
import { playUI, UI } from '../../utils/arena/uiSounds';
import AvatarImage from '../ui/AvatarImage';
import { playSound } from '../../utils/game/sounds';
import {
  rollD20, decideWinner, winnerToSeat,
} from '../../utils/game/firstPlayerRoll';
import InitiativeDiceStage from './InitiativeDiceStage';
import DiceAppearancePicker, { DICE_PANEL_SURFACE } from '../DiceAppearancePicker';
import { onDicePaletteChange } from '../../utils/game/dice3d/dieAssets';
import { reveal, rollEnd, rollStart } from '../../utils/game/dice3d/diceAudio';
import VikingOrnament from '../VikingOrnament';
import PlayerTitle from '../PlayerTitle';
import { t } from '../../localization/t';
import TarotCard from './TarotCard';
import ForgedButton from '../ui/ForgedButton';
import { PREDICTIONS } from '../../utils/arena/oracle';

/**
 * BG3-style D20 modal that picks the starting player. Each seat
 * rolls its own die independently. The player owning that seat
 * clicks. In sandbox the local user pilots both seats; in 1v1 the
 * remote die only animates when the peer's roll broadcast arrives.
 * Once both seats have rolled the winner is computed; on a tie
 * both rolls clear and both players roll again.
 *
 * After a non-tie roll the *winner* picks who plays first (game
 * rule. The roll grants the right of choice, not initiative
 * itself). The loser waits on the same modal showing "choosing…"
 * until the winner's pick broadcasts back, then both clients exit
 * with the agreed firstPlayerSeat.
 *
 * Props:
 *   localName, remoteName             : string
 *   localAvatarUrl, remoteAvatarUrl   : string | null  (sorcery card image)
 *   isHost                : boolean. Local user is host (seat 1)
 *   isSandbox             : boolean. Solo flow, both dice clickable
 *   onResult({ winnerSeat, firstPlayerSeat, hostRoll, guestRoll })
 *   isAuthMatch           : boolean. Server-authoritative roll-off (flag on,
 *                                       not sandbox). When true, rolls + choice
 *                                       route through the server commands below
 *                                       instead of the symmetric P2P broadcasts.
 *   requestServerRoll()     : () => void. Ask the server to arbitrate the
 *                                                roll (auth mode); rolls arrive
 *                                                back via the mirror subscription.
 *   commitFirstPlayer(n)    : (1|2) => void. Commit the winner's first-player
 *                                                pick to the server (auth mode).
 *   subscribeToRoll(fn)     : (fn) => unsubscribe. Receive remote seat rolls
 *   subscribeToChoice(fn)   : (fn) => unsubscribe. Receive remote choice
 *   onLeaveMatch()          : () => void. Opens the "Leave game?" confirm
 *                                               (forced-ceremony exit hatch:
 *                                               the modal can't be dismissed
 *                                               by other means, so this is
 *                                               the only way out before the
 *                                               match actually starts)
 *   localPalette, remotePalette : DICE_PALETTES id each seat's d20 wears, or null
 *   initialRollState        : { hostRoll, guestRoll, winnerSeat, firstPlayerSeat } | null
 * Seeds the modal when resuming
 *                                              a session that was saved mid-
 *                                              roll. Any subset of fields may
 *                                              be non-null; missing fields fall
 *                                              back to a fresh-match start.
 *   onRollStateChange(state): (state) => void. Fires after every persistable
 *                                              roll-state advance so GameBoard
 *                                              can mirror it into its save
 *                                              snapshot. Same shape as
 *                                              initialRollState.
 */

const PHASE = {
  ENTRY: 'entry',
  IDLE: 'idle',
  RESOLVED: 'resolved',   // dice landed, winner identified, awaiting choice
  CONFIRMED: 'confirmed', // first player chosen, brief hold before exit
  EXITING: 'exiting',
};

const ENTRY_DURATION_MS = 600;
const TIE_HOLD_MS = 900;
const CONFIRMED_HOLD_MS = 1100;
const EXIT_DURATION_MS = 500;
// Minimum time the "rolling" state stays visible on the receiver,
// matching the natural duration of a tray throw. This
// is the safety floor. If the dice ref happens to be unready (e.g.
// a peer's broadcast arrives in the few frames between modal mount
// and child mount) the receiver still observes "Rolling…" for the
// full tumble before the chip resolves.
const MIN_ROLL_VISIBLE_MS = 1900;

// ─── Dice card geometry ────────────────────────────────────
//
// Each seat gets its own floating card, and the card IS that die's
// tray: the canvas covers it edge to edge, so a throw ricochets off
// the card's four borders. Banging wood and spraying sparks at each
// wall. Runs over the name and the chip, and comes home to the slot
// in the middle. See InitiativeDiceStage.
const CARD_WIDTH_CQW = 20;
const CARD_GAP_CQW = 2.5;
// The blocks above and below the dice band are the SAME height. World
// origin is the centre of the canvas, so this is what puts the resting
// die (and the ornament, and the dish) at the card's exact centre.
// Avatar + name + title come to ~7.4cqw; the block has to clear that or the
// portrait rides up onto the card's border.
const CARD_END_BLOCK_CQW = 7.6;
// Breathing room inside the card's border. Equal top and bottom, so the die
// still rests on the card's exact centre.
const CARD_PAD_CQW = 1;
// A hair smaller than the 5cqw portrait elsewhere: on a 20cqw card that one
// reads as a bust rather than an avatar, and it crowds the name.
const CARD_AVATAR_CQW = 4.2;
const DICE_BAND_CQW = 10;
// Half the world width the camera frames. Larger = smaller die on a
// wider tray; 2.5 puts a radius-0.92 die at roughly 7.4cqw across on a
// 20cqw-wide card.
const TRAY_VIEW_HALF_WIDTH = 2.5;

/**
 * The engraving fades out before it reaches the card's border, the way the
 * almanac-cover rings do in the other app. Without it the ornament is chopped
 * off by the edge and reads as a cropped picture instead of as something the
 * surface was stamped with.
 */
const ENGRAVING_FADE =
  'radial-gradient(ellipse 74% 74% at 50% 50%, #000 34%, rgb(0 0 0 / 0.4) 74%, rgb(0 0 0 / 0) 100%)';

/** A d20 roll-off has two results worth a flourish. */
const critOf = (value) => (value === 20 ? 'high' : value === 1 ? 'low' : null);

/** Warmer than the card's gold: the ember the winner's engraving glows with. */
const AMBER = 'rgba(255, 168, 74, 1)';

/** The wash a natural 20 / natural 1 leaves on the card it landed in. */
const CRIT_GLOW = {
  high: `radial-gradient(ellipse 70% 50% at 50% 50%, ${GOLD} 0.28), transparent 72%)`,
  low: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(122, 47, 34, 0.34), transparent 72%)',
};

/**
 * Which side is muted once the roll-off resolves: everyone except the
 * dice-winner and, in CONFIRMED, the chosen first player. Pure, so
 * render and componentDidUpdate can't drift apart. The latter needs it
 * to mute the losing die's material on its tray.
 */
function dimFlags(state, props) {
  const { phase, winnerSeat, firstPlayerSeat } = state;
  const showConfirmed = phase === PHASE.CONFIRMED || phase === PHASE.EXITING;
  if (phase !== PHASE.RESOLVED && !showConfirmed) return { local: false, remote: false };
  const localIsHost = props.isHost || props.isSandbox;
  const dim = (seat) => winnerSeat !== seat && !(showConfirmed && firstPlayerSeat === seat);
  return { local: dim(localIsHost ? 1 : 2), remote: dim(localIsHost ? 2 : 1) };
}

export default class FirstPlayerDiceModal extends Component {
  // One tray per card. The local player is always drawn on the left.
  localStageRef = createRef();

  remoteStageRef = createRef();

  constructor(props) {
    super(props);
    const init = props.initialRollState || {};
    const hostRoll = typeof init.hostRoll === 'number' ? init.hostRoll : null;
    const guestRoll = typeof init.guestRoll === 'number' ? init.guestRoll : null;
    const winnerSeat = init.winnerSeat === 1 || init.winnerSeat === 2 ? init.winnerSeat : null;
    const firstPlayerSeat = init.firstPlayerSeat === 1 || init.firstPlayerSeat === 2
      ? init.firstPlayerSeat
      : null;

    // Initial phase reflects the most-advanced point captured in the
    // resumed snapshot. firstPlayer chosen → CONFIRMED (hold briefly
    // then exit); winner-only → RESOLVED (await pick); partial rolls
    // → IDLE, skipping the entrance animation so resume feels instant.
    // No resumed state → ENTRY for the fresh-match entrance dance.
    let phase = PHASE.ENTRY;
    if (firstPlayerSeat != null) phase = PHASE.CONFIRMED;
    else if (winnerSeat != null) phase = PHASE.RESOLVED;
    else if (hostRoll != null || guestRoll != null) phase = PHASE.IDLE;

    this.state = {
      phase,
      hostRoll,
      guestRoll,
      hostRolling: false,    // tumble animation in flight
      guestRolling: false,
      winnerSeat,
      tiePending: false,     // matched values shown briefly before clearing
      firstPlayerSeat,
      leaveHovered: false,   // pointer hover on the corner "Leave match" hatch
    };
    this._timers = [];
  }

  componentDidMount() {
    this._unsubPalette = onDicePaletteChange((palette) => this.setState({ chosenPalette: palette.id }));
    playUI(UI.OPEN);
    this.syncDim();

    if (this.state.phase === PHASE.ENTRY) {
      this._timer(() => this.setState({ phase: PHASE.IDLE }), ENTRY_DURATION_MS);
    } else if (this.state.phase === PHASE.CONFIRMED) {
      // Resumed after the first player was chosen but the modal didn't
      // get to exit before the previous session ended. Hold briefly so
      // the user sees the result, then exit normally. OnResult fires
      // and GameBoard's resolver completes the opening flow.
      this._timer(() => this.startExit(), CONFIRMED_HOLD_MS);
    } else if (this.state.hostRoll != null && this.state.guestRoll != null) {
      // Resumed from a snapshot captured between the second roll landing
      // and the winner being persisted (or mid-tie-display): both values
      // are locked in but winnerSeat is null, so the constructor seeded
      // IDLE. Nothing is clickable in that state and no further broadcast
      // is coming. Recompute the resolution now. A tie clears both rolls
      // for a fresh re-roll, exactly as the live path does.
      this.checkResolution();
    }
    // RESOLVED on mount: winner is known, awaiting pick. Winner clicks
    // a choice or peer's broadcast arrives via onRemoteChoice.
    // IDLE on mount with partial rolls: waits for the remaining seat.

    if (this.props.subscribeToRoll) {
      this._unsubRoll = this.props.subscribeToRoll((msg) => this.onRemoteSeatRoll(msg));
    }
    if (this.props.subscribeToChoice) {
      this._unsubChoice = this.props.subscribeToChoice((msg) => this.onRemoteChoice(msg));
    }
  }

  componentDidUpdate() {
    this.syncDim();
  }

  // The die is drawn in WebGL, so a losing card's CSS filter doesn't
  // reach it. The tray dims it in the material instead. Pushed from
  // the lifecycle rather than from render
  // so render stays free of side effects. Runs on mount too: resuming a
  // saved session lands straight in RESOLVED/CONFIRMED, and the loser's
  // die should already be muted on the first frame.
  syncDim() {
    const { local, remote } = dimFlags(this.state, this.props);
    if (local === this._dimmedLocal && remote === this._dimmedRemote) return;
    this._dimmedLocal = local;
    this._dimmedRemote = remote;
    this.localStageRef.current?.setDim(local);
    this.remoteStageRef.current?.setDim(remote);
  }

  componentWillUnmount() {
    this._unsubPalette?.();
    // The wheel is the modal's, not a tray's. Leaving mid-throw has to stop
    // it, or it keeps clicking over dice nobody can see.
    rollEnd();
    this._timers.forEach((t) => clearTimeout(t));
    if (this._unsubRoll) this._unsubRoll();
    if (this._unsubChoice) this._unsubChoice();
    this._unmounted = true;
  }

  _timer(fn, ms) {
    const id = setTimeout(() => {
      if (this._unmounted) return;
      fn();
    }, ms);
    this._timers.push(id);
    return id;
  }

  // Fires after every setState that advances persistable roll fields
  // (rolls, winner, firstPlayer). The parent mirrors this into its
  // save snapshot so an app close mid-roll resumes into the same
  // modal state instead of a corrupt pre-dice limbo.
  _emitRollState = () => {
    if (!this.props.onRollStateChange) return;
    const { hostRoll, guestRoll, winnerSeat, firstPlayerSeat } = this.state;
    this.props.onRollStateChange({ hostRoll, guestRoll, winnerSeat, firstPlayerSeat });
  };

  // Which seat the local user owns. In sandbox the user controls
  // both seats, but `localSeat` still represents "the side on the
  // left." The click handlers route the other seat through
  // onRemoteDieClick.
  localSeat() {
    return (this.props.isHost || this.props.isSandbox) ? 'host' : 'guest';
  }

  // ─── Roll trigger paths ────────────────────────────────

  // Roll a specific seat locally: generate value, animate the die,
  // broadcast (in MP). Guards against rolling the same seat twice
  // and against rolling while the phase is past IDLE.
  rollSeat = async (seat) => {
    if (this.state.phase !== PHASE.IDLE) return;
    const rolledKey = seat === 'host' ? 'hostRoll' : 'guestRoll';
    const rollingKey = seat === 'host' ? 'hostRolling' : 'guestRolling';
    if (this.state[rolledKey] != null) return;
    if (this.state[rollingKey]) return;

    // Auth match: the server arbitrates the roll. Request it ONCE per die
    // (guarded against double-click) and return. We do NOT roll locally,
    // animate, or broadcast. The server's rolls arrive via the mirror
    // subscription and animate through the existing onRemoteSeatRoll path.
    // In sandbox (solo) the ONE pilot clicks BOTH dice, so the request names
    // the die's seat and the guard is per-seat.
    if (this.props.isAuthMatch) {
      if (!this._serverRollRequested) this._serverRollRequested = {};
      if (this._serverRollRequested[seat]) return;
      this._serverRollRequested[seat] = true;
      this.props.requestServerRoll?.(seat === 'guest' ? 2 : 1);
      return;
    }

    const value = rollD20();
    this.setState({ [rollingKey]: true });
    await this.animateSeatRoll(seat, value);
  };

  // Receive a peer's seat roll. Per-seat state guards below are
  // sufficient to prevent double-application. We deliberately don't
  // dedup by rollId because each client's rollId counter is local,
  // so both peers can independently emit rollId=1, rollId=2, etc.
  // for different seats. A rollId-based dedup would collide across
  // clients and reject the second peer's first roll.
  onRemoteSeatRoll = async (msg) => {
    if (!msg || (msg.seat !== 'host' && msg.seat !== 'guest')) return;
    // Server TIE reset (auth): the reducer cleared this seat's roll so both
    // seats re-roll. Handled before the phase guards. A reset must land even
    // when the modal already left IDLE (e.g. it self-resolved off the rolls).
    if (msg.reset) {
      this._applyServerRollReset(msg.seat);
      return;
    }
    if (typeof msg.value !== 'number') return;
    if (this.state.phase !== PHASE.IDLE && this.state.phase !== PHASE.ENTRY) return;
    const rolledKey = msg.seat === 'host' ? 'hostRoll' : 'guestRoll';
    const rollingKey = msg.seat === 'host' ? 'hostRolling' : 'guestRolling';
    if (this.state[rolledKey] != null || this.state[rollingKey]) return;

    this.setState({ [rollingKey]: true });
    await this.animateSeatRoll(msg.seat, msg.value);
  };

  // Server tie reset for one seat: clear the die, drop any locally-resolved
  // winner, re-arm the roll-request guard, and fall back to a rollable phase.
  // The epoch bump discards a mid-flight animation's landing so it can't
  // resurrect the discarded value. Never rewinds a confirmed choice.
  _applyServerRollReset(seat) {
    this._rollEpoch = (this._rollEpoch || 0) + 1;
    this._serverRollRequested = {};
    if (this.state.firstPlayerSeat != null) return;
    const rolledKey = seat === 'host' ? 'hostRoll' : 'guestRoll';
    const rollingKey = seat === 'host' ? 'hostRolling' : 'guestRolling';
    if (this.state[rolledKey] == null && !this.state[rollingKey] && this.state.phase === PHASE.IDLE) return;
    this.setState({
      [rolledKey]: null,
      [rollingKey]: false,
      winnerSeat: null,
      tiePending: false,
      phase: this.state.phase === PHASE.ENTRY ? PHASE.ENTRY : PHASE.IDLE,
    }, () => this._emitRollState());
  }

  // Shared animation pipeline. Used by both local and remote roll
  // paths so the visual is identical regardless of who initiated.
  // Enforces a minimum visible-tumble duration so the result chip
  // never just pops in: even if the WebGL canvas isn't ready yet
  // (e.g. a remote broadcast arrives in the few frames between
  // modal mount and child mount), the rolling state is held for
  // at least MIN_ROLL_VISIBLE_MS before the value lands.
  // Which card holds `seat`'s die. The local player is always drawn on
  // the left, so the mapping depends on which seat the local user owns.
  stageFor(seat) {
    const localIsHost = this.props.isHost || this.props.isSandbox;
    return (seat === 'host') === localIsHost ? this.localStageRef : this.remoteStageRef;
  }

  /**
   * The wheel is ONE sound for the whole roll-off, but there is a tray per
   * seat and neither knows about the other. So the modal owns it. Starting a
   * throw re-lays the wheel over the longest flight still in the air, and it
   * only stops once no tray is flying: two seats can be mid-throw at once and
   * the first landing must not cut the second's sound.
   */
  syncWheel() {
    const longest = Math.max(
      this.localStageRef.current?.flightRemaining() ?? 0,
      this.remoteStageRef.current?.flightRemaining() ?? 0,
    );
    if (longest > 0) rollStart(longest);
    else rollEnd();
  }

  async animateSeatRoll(seat, value) {
    const epoch = this._rollEpoch || 0;
    const startedAt = performance.now();
    const stage = this.stageFor(seat).current;
    if (stage) {
      try {
        // The die's own foley (cushion bangs, rattle) lives in the tray. It
        // has to fire on the impacts, which only the simulation knows.
        const flight = stage.roll(value, critOf(value));
        this.syncWheel();
        await flight;
        this.syncWheel();
      } catch (err) {
        console.warn('[FirstPlayerDice] roll animation threw:', err);
      }
    } else {
      playSound('diceRoll');
    }
    const elapsed = performance.now() - startedAt;
    if (elapsed < MIN_ROLL_VISIBLE_MS) {
      await this._wait(MIN_ROLL_VISIBLE_MS - elapsed);
    }
    if (this._unmounted) return;
    // A server tie reset landed while this die was tumbling. The value was
    // discarded authoritatively; letting it land would resurrect a dead roll.
    if ((this._rollEpoch || 0) !== epoch) return;

    const rolledKey = seat === 'host' ? 'hostRoll' : 'guestRoll';
    const rollingKey = seat === 'host' ? 'hostRolling' : 'guestRolling';
    // The rattle said the die is lying there; the verdict says a number now
    // stands. Two different things. And the chip appearing is the second.
    reveal(critOf(value));
    this.setState(
      { [rolledKey]: value, [rollingKey]: false },
      () => {
        this._emitRollState();
        this.checkResolution();
      },
    );
  }

  _wait(ms) {
    return new Promise((resolve) => this._timer(resolve, ms));
  }

  // Called whenever a seat finishes rolling. If both seats now have
  // a value, compute the winner. Tie → brief hold, then clear both
  // rolls so each side can click to roll again.
  checkResolution = () => {
    const { hostRoll, guestRoll } = this.state;
    if (hostRoll == null || guestRoll == null) return;
    playSound('uiClick');

    const winner = decideWinner(hostRoll, guestRoll);
    if (winner === 'tie') {
      // Hold the matched values briefly so both players can see the
      // tie, then clear both rolls. Each side then clicks their die
      // again to re-roll.
      this.setState({ tiePending: true });
      this._timer(() => {
        if (this._unmounted) return;
        // Re-enable the per-seat server-roll requests so the player can
        // roll again after a tie (auth match. The single-shot guard is set on
        // request and only cleared here).
        this._serverRollRequested = {};
        this.setState({
          hostRoll: null,
          guestRoll: null,
          winnerSeat: null,
          tiePending: false,
        }, () => this._emitRollState());
      }, TIE_HOLD_MS);
      return;
    }

    this.setState({
      winnerSeat: winnerToSeat(winner),
      phase: PHASE.RESOLVED,
    }, () => {
      this._emitRollState();
      // A reconnect state-sync may have delivered the winner's pick
      // before our rolls had finished animating; in that window the
      // choice was queued. Now that we're in RESOLVED, drain it so
      // the modal lands in CONFIRMED instead of waiting forever for
      // a peer broadcast that already happened.
      if (this._pendingResumedChoice != null) {
        const seat = this._pendingResumedChoice;
        this._pendingResumedChoice = null;
        this.onRemoteChoice({ firstPlayerSeat: seat });
      }
    });
  };

  // The dice-roll winner picks who plays first. In MP only the
  // winner's client takes this path. The loser receives the choice
  // via onRemoteChoice. In sandbox the user pilots both seats so
  // they pick directly from their own modal regardless of winner.
  pickFirstPlayer = (firstPlayerSeat) => {
    if (this.state.phase !== PHASE.RESOLVED) return;
    if (this.state.firstPlayerSeat != null) return;
    if (firstPlayerSeat !== 1 && firstPlayerSeat !== 2) return;

    playUI(UI.CONFIRM);

    // Auth match: commit the pick and WAIT. The authoritative SET_FIRST_PLAYER
    // diff confirms via onRemoteChoice on BOTH clients, so neither side can
    // enter the game on a pick the server rejected (e.g. a stale-roll "winner"
    // that isn't the server's rollOff winner). A rejection is a silent no-op:
    // the modal stays RESOLVED and the pick remains clickable.
    if (this.props.isAuthMatch) {
      this.props.commitFirstPlayer?.(firstPlayerSeat);
      return;
    }

    this.setState({ firstPlayerSeat, phase: PHASE.CONFIRMED }, () => this._emitRollState());
    this._timer(() => this.startExit(), CONFIRMED_HOLD_MS);
  };

  onRemoteChoice = (msg) => {
    if (!msg) return;
    const seat = msg.firstPlayerSeat;
    if (seat !== 1 && seat !== 2) return;
    // Only the loser's modal applies an inbound choice. The winner
    // already drove this state locally via pickFirstPlayer; ignoring
    // here keeps the broadcast harmless if any server-side fanout
    // ever echoes it back.
    if (this.state.firstPlayerSeat != null) return;
    if (this.state.phase !== PHASE.RESOLVED) {
      // Pre-RESOLVED arrival happens on reconnect: the peer's state
      // sync may carry both rolls + the choice all at once, and the
      // rolls still need to animate before the winner is computed.
      // Park the choice and apply it from checkResolution once the
      // RESOLVED transition lands.
      this._pendingResumedChoice = seat;
      return;
    }

    this.setState({ firstPlayerSeat: seat, phase: PHASE.CONFIRMED }, () => this._emitRollState());
    this._timer(() => this.startExit(), CONFIRMED_HOLD_MS);
  };

  startExit = () => {
    if (this._unmounted) return;
    if (this.state.phase === PHASE.EXITING) return;
    this.setState({ phase: PHASE.EXITING });
    this._timer(() => {
      const { onResult } = this.props;
      if (onResult) {
        onResult({
          winnerSeat: this.state.winnerSeat,
          firstPlayerSeat: this.state.firstPlayerSeat ?? this.state.winnerSeat,
          hostRoll: this.state.hostRoll,
          guestRoll: this.state.guestRoll,
        });
      }
    }, EXIT_DURATION_MS);
  };

  // ─── Click handlers ────────────────────────────────────

  // Clicking the left/local die rolls the local seat. In MP, this
  // is the host's die for the host client, the guest's die for the
  // guest client. In sandbox, it's seat 1 (host).
  onLocalDieClick = () => {
    this.rollSeat(this.localSeat());
  };

  // Clicking the right/remote die is only meaningful in sandbox.
  // the user pilots both seats there, so they click both dice
  // themselves. In MP the remote die is not clickable.
  onRemoteDieClick = () => {
    if (!this.props.isSandbox) return;
    const remote = this.localSeat() === 'host' ? 'guest' : 'host';
    this.rollSeat(remote);
  };

  // ─── Render ────────────────────────────────────────────

  render() {
    const {
      phase, hostRoll, guestRoll, winnerSeat,
      hostRolling, guestRolling, tiePending,
      firstPlayerSeat,
    } = this.state;
    const {
      localName, remoteName,
      localAvatarUrl, remoteAvatarUrl,
      localPlayer, remotePlayer,
      localPalette, remotePalette,
      sorceryCards,
      isHost, isSandbox,
    } = this.props;

    // Map seat to which side of the modal. Local is always on the
    // left so the player's name reads consistently.
    const localIsHost = isHost || isSandbox;
    const localSeat = localIsHost ? 1 : 2;
    const remoteSeat = localIsHost ? 2 : 1;
    const localRoll = localIsHost ? hostRoll : guestRoll;
    const remoteRoll = localIsHost ? guestRoll : hostRoll;
    const localRolling = localIsHost ? hostRolling : guestRolling;
    const remoteRolling = localIsHost ? guestRolling : hostRolling;
    const localWon = winnerSeat != null && winnerSeat === localSeat;
    const remoteWon = winnerSeat != null && winnerSeat === remoteSeat;
    const localPlaysFirst = firstPlayerSeat != null && firstPlayerSeat === localSeat;
    const remotePlaysFirst = firstPlayerSeat != null && firstPlayerSeat === remoteSeat;
    const winnerName = winnerSeat == null ? null : (localWon ? localName : remoteName);
    const loserName = winnerSeat == null ? null : (localWon ? remoteName : localName);
    const firstPlayerName = firstPlayerSeat == null
      ? null
      : (firstPlayerSeat === localSeat ? localName : remoteName);

    // Local user is the one who picks (and in sandbox they always
    // are, since they pilot both seats). Drives whether we render
    // choice buttons or a "waiting for opponent" status.
    const localChooses = isSandbox || localWon;
    const showChoiceUi = phase === PHASE.RESOLVED;
    const showConfirmed = phase === PHASE.CONFIRMED || phase === PHASE.EXITING;
    const resolvedOrAfter = showChoiceUi || showConfirmed;

    // In CONFIRMED phase the first-player seat takes the spotlight
    // (un-dim, keeps name color highlight). The dice-winner's halo
    // and gold chip persist. They remain a record of the roll, the
    // PLAYS FIRST badge announces the chosen turn order.
    const { local: localDim, remote: remoteDim } = dimFlags(this.state, this.props);

    // A seat is clickable when the modal is idle, no tie is being
    // displayed, that seat hasn't rolled yet, and its die isn't
    // already mid-tumble. In MP only the local die is interactive;
    // the remote die animates from peer broadcasts. In sandbox
    // both are interactive.
    // An Oracle calls the match before touching the die.
    const { prediction } = this.props;
    const predictionMissing = !!prediction && !prediction.value;
    const localClickable = phase === PHASE.IDLE
      && !tiePending
      && localRoll == null
      && !localRolling
      && !predictionMissing;
    const remoteClickable = isSandbox
      && phase === PHASE.IDLE
      && !tiePending
      && remoteRoll == null
      && !remoteRolling;

    const visible = phase !== PHASE.EXITING;
    const entered = phase !== PHASE.ENTRY;

    // Nothing is a panel any more: the title, the two dice cards, the
    // prompt and the buttons are separate elements floating over a
    // blurred board. Each floats in on its own beat so the roll-off
    // assembles rather than appearing.
    const float = (delayMs) => ({
      transform: entered && visible ? 'translateY(0) scale(1)' : 'translateY(1.6cqw) scale(0.96)',
      opacity: entered && visible ? 1 : 0,
      transition: [
        `transform ${ENTRY_DURATION_MS}ms cubic-bezier(0.2, 0.9, 0.25, 1.05) ${delayMs}ms`,
        `opacity ${ENTRY_DURATION_MS}ms ease ${delayMs}ms`,
      ].join(', '),
    });

    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1.6cqw',
          background: visible
            ? 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.74) 100%)'
            : 'rgba(0,0,0,0)',
          // The board behind is pushed right out of focus: with no panel
          // to sit on, the floating pieces need the depth of field to
          // read as being in front of something rather than pasted onto
          // it. Desaturating with it keeps the board from competing with
          // the gold.
          backdropFilter: visible ? 'blur(14px) saturate(0.7) brightness(0.75)' : 'none',
          WebkitBackdropFilter: visible ? 'blur(14px) saturate(0.7) brightness(0.75)' : 'none',
          opacity: visible ? 1 : 0,
          transition: `opacity ${EXIT_DURATION_MS}ms ease, background ${EXIT_DURATION_MS}ms ease, backdrop-filter ${EXIT_DURATION_MS}ms ease`,
          pointerEvents: visible ? 'auto' : 'none',
        }}
      >
        <div
          className="arena-heading"
          style={{
            textAlign: 'center',
            fontSize: '3cqw',
            fontWeight: 700,
            letterSpacing: '0.02em',
            lineHeight: 1.05,
            background: 'linear-gradient(180deg, #fbe9b8 0%, #e8c466 55%, #b9893a 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: `drop-shadow(0 2px 6px rgba(0,0,0,0.85)) drop-shadow(0 0 18px ${GOLD} 0.3))`,
            ...float(0),
          }}
        >
          {predictionMissing ? t('Foretell the match') : t('Roll for Initiative')}
        </div>

        {/* The Oracle's call, its own step before the roll-off: two plates,
            one kept and sealed, then the trays come out. */}
        {predictionMissing ? (
          <div className="flex items-start justify-center" style={{ gap: '2.5cqw', paddingBottom: '4cqw', ...float(90) }} data-testid="oracle-prediction">
            {Object.entries(PREDICTIONS).map(([key, def]) => {
              const pick = this.state.predictionPick;
              return (
                <div key={key} className="relative">
                  <TarotCard
                    art={def.art}
                    name={t(def.title)}
                    width={15}
                    selected={pick === key}
                    dimmed={Boolean(pick) && pick !== key}
                    onClick={!prediction.busy
                      ? () => { playUI(UI.CLICK, { volume: 0.6 }); this.setState((st) => ({ predictionPick: st.predictionPick === key ? null : key })); }
                      : undefined}
                    testId={`prediction-${key}`}
                  />
                  {pick === key ? (
                    <div className="absolute left-1/2 -translate-x-1/2" style={{ top: '100%', marginTop: '0.875cqw' }}>
                      <ForgedButton className="px-6 py-3 text-[1.125cqw]" disabled={prediction.busy} onClick={() => prediction.onPick(key)}>
                        {t('Seal the call')}
                      </ForgedButton>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        {predictionMissing ? null : (<>
        {/* The two trays. Each card is its own table: the die shoots off
            the slot in its middle, ricochets off that card's four edges
            and comes home. They stagger in, left then right. */}
        <div style={{ position: 'relative', display: 'flex', gap: `${CARD_GAP_CQW}cqw`, alignItems: 'flex-start' }}>
          <div data-dice-appearance-anchor style={{ position: 'relative' }}>
            <div className="vh-dice-initiative-controls" style={{ ...DICE_PANEL_SURFACE, ...float(90) }}>
              <DiceAppearancePicker side="left" />
            </div>
            <DiceCard
              stageRef={this.localStageRef}
              name={localName}
              avatarUrl={localAvatarUrl}
              player={localPlayer}
              palette={this.state.chosenPalette ?? localPalette}
              sorceryCards={sorceryCards}
              isLocal
              clickable={localClickable}
              rolling={localRolling}
              roll={localRoll}
              dim={localDim}
              wins={localWon && resolvedOrAfter}
              playsFirst={showConfirmed && localPlaysFirst}
              phase={phase}
              isSandbox={isSandbox}
              onDieClick={this.onLocalDieClick}
              promptOverride={predictionMissing && phase === PHASE.IDLE ? t('Call the match first') : null}
              style={float(90)}
            />
          </div>
          <DiceCard
            stageRef={this.remoteStageRef}
            name={remoteName}
            avatarUrl={remoteAvatarUrl}
            player={remotePlayer}
            palette={remotePalette}
            sorceryCards={sorceryCards}
            clickable={remoteClickable}
            rolling={remoteRolling}
            roll={remoteRoll}
            dim={remoteDim}
            wins={remoteWon && resolvedOrAfter}
            playsFirst={showConfirmed && remotePlaysFirst}
            phase={phase}
            isSandbox={isSandbox}
            onDieClick={this.onRemoteDieClick}
            style={float(170)}
          />
        </div>
        </>)}

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1cqw', ...float(240) }}>
          {predictionMissing ? null : <FooterPrompt
            phase={phase}
            remoteName={remoteName}
            winnerName={winnerName}
            firstPlayerName={firstPlayerName}
            localChooses={localChooses}
            tiePending={tiePending}
          />}

          {/* Choice buttons for the winner, status for the loser, or
              nothing once the pick is confirmed. */}
          {predictionMissing ? null : <ActionZone
            phase={phase}
            isSandbox={isSandbox}
            localChooses={localChooses}
            winnerName={winnerName}
            loserName={loserName}
            remoteName={remoteName}
            winnerSeat={winnerSeat}
            localSeat={localSeat}
            remoteSeat={remoteSeat}
            onPick={this.pickFirstPlayer}
          />}

          {/* Forced-ceremony exit hatch. The roll-off blocks the rest of
              the game-board UI (incl. the normal Leave button), so
              without this the player has no way to abandon the match
              before it starts. Defers to GameBoard.requestExit which
              opens the standard "Leave game?" confirm. Save & Leave or
              Cancel. So this isn't a one-click footgun. */}
          {this.props.onLeaveMatch && phase !== PHASE.EXITING ? (
            <button
              type="button"
              onClick={this.props.onLeaveMatch}
              onMouseEnter={() => this.setState({ leaveHovered: true })}
              onMouseLeave={() => this.setState({ leaveHovered: false })}
              style={{
                background: this.state.leaveHovered ? `${GOLD} 0.12)` : 'rgba(12, 10, 8, 0.55)',
                border: `1px solid ${GOLD} ${this.state.leaveHovered ? '0.7)' : '0.35)'}`,
 borderRadius: 'var(--radius-md)',
 padding: '0.45cqw 1.4cqw',
 fontSize: '0.8cqw',
 fontWeight: 700,
 color: this.state.leaveHovered ? ACCENT_GOLD : PARCHMENT + ' 0.8)',
 boxShadow: '0 8px 24px rgba(0,0,0,0.55)',
 transition: 'color 200ms ease, background 200ms ease, border-color 200ms ease',
 textShadow: '0 1px 2px rgba(0,0,0,0.6)',
 }}
 >
 {t('Leave Match')}
 </button>
 ) : null}
 </div>
 </div>
 );
 }
}

// ─── The floating dice card ────────────────────────────────
//
// One card per seat, and the card IS the tray: the canvas covers it
// edge to edge (see InitiativeDiceStage), so the die runs over the
// name, over the result chip and right up to the card's border before
// coming home to the slot in the middle.
//
// Which is why the card is built as three stacked blocks of EQUAL
// height above and below the dice band: world origin is the centre of
// the canvas, so the die rests at the card's exact vertical centre.
// and that is also where the ornament and the dish are centred.

function DiceCard({
 stageRef, name, avatarUrl, player, palette, sorceryCards, isLocal,
 clickable, rolling, roll, dim, wins, playsFirst, phase, isSandbox,
 onDieClick, promptOverride, style,
}) {
 const showRoll = roll != null;
 // Per-card prompt rules:
 // - This seat is currently rolling → "Rolling…"
 // - This seat is clickable (own, not yet rolled, no tie pending) → "Click to roll"
 // - Remote seat in MP that hasn't rolled yet → "Waiting…"
 // - Otherwise blank
 let promptText = '';
 if (!showRoll && phase === PHASE.IDLE) {
 if (rolling) promptText = t('Rolling…');
 else if (promptOverride) promptText = promptOverride;
 else if (clickable) promptText = t('Click to roll');
 else if (!isLocal && !isSandbox) promptText = t('Waiting…');
 }

 return (
 <div style={style}>
 <div
 role={clickable ? 'button' : undefined}
 tabIndex={clickable ? 0 : undefined}
 aria-label={clickable ? (name ? t('Roll {name} die', { name }) : isLocal ? t('Roll your die') : t('Roll opponent die')) : undefined}
        onClick={clickable ? onDieClick : undefined}
        onKeyDown={clickable
          ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDieClick?.(); } }
          : undefined}
        style={{
          position: 'relative',
          isolation: 'isolate',
          overflow: 'hidden',
          width: `${CARD_WIDTH_CQW}cqw`,
          background: `${CONTENT_BG_DEFAULT}, ${PANEL_BG}`,
          backgroundBlendMode: 'overlay, normal',
          border: `1px solid ${GOLD} ${wins ? '0.9)' : '0.45)'}`,
          borderRadius: 'var(--radius-lg)',
          padding: `${CARD_PAD_CQW}cqw 0`,
          // Floating, not seated: a long soft drop shadow lifts the card
          // off the blurred board, and the winner gets a gold bloom.
          boxShadow: [
            '0 26px 70px rgba(0,0,0,0.72)',
            wins ? `0 0 46px ${GOLD} 0.42)` : `0 0 26px ${GOLD} 0.1)`,
            `inset 0 0 0 1px ${GOLD} ${wins ? '0.3)' : '0.16)'}`,
            'inset 0 0 46px rgba(0,0,0,0.55)',
          ].join(', '),
          filter: dim ? 'saturate(0.45) brightness(0.72)' : 'none',
          transform: wins ? 'translateY(-0.4cqw) scale(1.03)' : 'none',
          transition: [
            'transform 500ms cubic-bezier(0.2, 0.9, 0.25, 1.05)',
            'box-shadow 400ms ease',
            'border-color 400ms ease',
            'filter 400ms ease',
          ].join(', '),
        }}
      >
        {/* **The engraving behind the dice.** The heraldic medallion that
            used to sit faded behind the whole legacy panel now radiates
            from the exact point the die comes to rest, oversized so its
            outer rings run off the card instead of ending inside it.
            the almanac-cover treatment the other app puts behind its
            throws. */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            maskImage: ENGRAVING_FADE,
            WebkitMaskImage: ENGRAVING_FADE,
            // The winner's engraving warms to amber and breathes. The card
            // itself catching light, not another badge stuck onto it.
            animation: wins ? 'fpdEngravePulse 2.6s ease-in-out infinite' : 'none',
          }}
        >
          <VikingOrnament
            ornament="style2d007"
            variant="medallion"
            color={wins ? AMBER : `${GOLD} 1)`}
            opacity={wins ? 0.26 : 0.13}
            style={{
              inset: 'auto',
              top: '50%',
              left: '50%',
              width: `${CARD_WIDTH_CQW * 1.3}cqw`,
              height: `${CARD_WIDTH_CQW * 1.3}cqw`,
              transform: 'translate(-50%, -50%)',
            }}
          />
        </div>

        {/* The dish. It lies OVER the engraving. It is the shape of the
            surface, and a shadow falls on what is printed on it too. */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            background: [
              'radial-gradient(ellipse 80% 62% at 50% 50%, rgba(0,0,0,0.34), rgba(0,0,0,0.14) 54%, rgba(0,0,0,0) 84%)',
              'radial-gradient(ellipse 66% 38% at 48% 12%, rgba(0,0,0,0.3), rgba(0,0,0,0) 74%)',
              `radial-gradient(ellipse 66% 38% at 54% 88%, ${GOLD} 0.06), rgba(0,0,0,0) 74%)`,
            ].join(', '),
          }}
        />

        {/* The natural 20 / natural 1 tints the whole card. */}
        {showRoll && critOf(roll) !== null ? (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 0,
              pointerEvents: 'none',
              background: CRIT_GLOW[critOf(roll)],
              animation: 'fpdCritGlow 600ms ease',
            }}
          />
        ) : null}

        {/* Top block. Identity. Its height is mirrored by the bottom
            block so the dice band's centre is the card's centre. */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            height: `${CARD_END_BLOCK_CQW}cqw`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.35cqw',
            padding: '0 0.75cqw',
          }}
        >
          <Avatar url={avatarUrl} highlight={wins} dim={dim} size={CARD_AVATAR_CQW} />
          <div
            className="arena-heading"
            style={{
              fontSize: '1.35cqw',
              color: wins ? ACCENT_GOLD : (dim ? TEXT_MUTED : (PARCHMENT + ' 0.85)')),
              letterSpacing: '0.04em',
              fontWeight: 600,
              textShadow: wins
                ? `0 0 8px ${GOLD} 0.6), 0 1px 2px rgba(0,0,0,0.7)`
                : '0 1px 2px rgba(0,0,0,0.6)',
              maxWidth: `${CARD_WIDTH_CQW - 2}cqw`,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              transition: 'color 300ms ease',
            }}
          >
            {name}
          </div>
          {player?.id ? (
            <PlayerTitle
              player={player}
              sorceryCards={sorceryCards}
              prefix=""
              style={{ fontSize: '0.625cqw', maxWidth: `${CARD_WIDTH_CQW - 2}cqw`, opacity: dim ? 0.55 : 1 }}
            />
          ) : null}
        </div>

        {/* The resting band. It only holds the height free. The throw
            uses the whole card. */}
        <div style={{ height: `${DICE_BAND_CQW}cqw` }} />

        {/* Bottom block. The result. */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            height: `${CARD_END_BLOCK_CQW}cqw`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5cqw',
          }}
        >
          {showRoll ? (
            <RollChip key={`roll-${roll}`} value={roll} wins={wins} dim={dim} />
 ) : (
 <div style={{ fontSize: '0.85cqw', color: TEXT_MUTED }}>
 {promptText}
 </div>
 )}
 {/* Reserved slot so the card doesn't twitch when the badge lands. */}
 <div style={{ height: '2cqw', display: 'flex', alignItems: 'center' }}>
 {playsFirst ? <PlaysFirstBadge /> : null}
 </div>
 </div>

 <InitiativeDiceStage ref={stageRef} viewHalfWidth={TRAY_VIEW_HALF_WIDTH} palette={palette ?? null} />
 </div>

 <style>{KEYFRAMES}</style>
 </div>
 );
}

function PlaysFirstBadge() {
 return (
 <div
 style={{
 display: 'inline-flex',
 alignItems: 'center',
 gap: '0.4cqw',
 padding: '0.25cqw 1cqw',
 fontSize: '0.75cqw',
 fontWeight: 700,
 color: ACCENT_GOLD,
 background: 'linear-gradient(180deg, rgba(58, 40, 14, 0.95) 0%, rgba(38, 26, 10, 0.95) 100%)',
 border: `1px solid ${GOLD} 0.7)`,
        borderRadius: 'var(--radius-full)',
        boxShadow: `0 0 14px ${GOLD} 0.55), inset 0 1px 0 rgba(255,235,180,0.2)`,
        textShadow: `0 0 8px ${GOLD} 0.7), 0 1px 2px rgba(0,0,0,0.6)`,
        animation: 'fpdRevealNumeral 380ms cubic-bezier(0.2, 0.9, 0.25, 1.05)',
      }}
    >
      {t('Plays First')}
    </div>
  );
}

function ActionZone({
  phase, isSandbox, localChooses,
  winnerName, loserName, remoteName,
  winnerSeat, localSeat, remoteSeat, onPick,
}) {
  // Below the divider we show one of three things:
  //   - choice buttons (winner / sandbox in RESOLVED)
  //   - "X is choosing…" status (loser in MP, RESOLVED)
  //   - empty slot (everything else. IDLE while still rolling,
  //     CONFIRMED / EXITING after the pick lands)
  // The wrapper holds a fixed height so the modal doesn't twitch
  // when the contents swap.
  const visible = phase === PHASE.RESOLVED;

  let body = null;
  if (visible && localChooses && winnerSeat != null) {
    // Winner/sandbox: two options. In sandbox we label them with
    // the actual seat names since the user pilots both; in MP the
    // first-person "I'll go first" reads more naturally for the
    // winner.
    const firstLabel = isSandbox ? (winnerName || t('Winner')) : t('You');
    const secondLabel = isSandbox ? (loserName || t('Other')) : t('Opponent');
    const otherSeat = winnerSeat === localSeat ? remoteSeat : localSeat;
    const btnStyle = {
      width: '11cqw',
      padding: '0.6cqw 0',
      fontSize: '1.05cqw',
      fontWeight: 600,
      textAlign: 'center',
    };
    body = (
      <div style={{ display: 'flex', gap: '0.8cqw' }}>
        <MedievalBtn variant="gold" onClick={() => onPick(winnerSeat)} style={btnStyle}>
          {firstLabel}
        </MedievalBtn>
        <MedievalBtn variant="default" onClick={() => onPick(otherSeat)} style={btnStyle}>
          {secondLabel}
        </MedievalBtn>
      </div>
    );
  } else if (visible && !localChooses) {
    body = (
      <div
        style={{
          fontSize: '0.95cqw',
          color: PARCHMENT + ' 0.7)',
          fontStyle: 'italic',
          letterSpacing: '0.08em',
        }}
      >
        {winnerName ? t('{name} is choosing…', { name: winnerName }) : t('Opponent is choosing…')}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '1cqw',
        minHeight: '2.4cqw',
        opacity: visible ? 1 : 0,
        transition: 'opacity 300ms ease',
      }}
    >
      {body}
    </div>
  );
}

function Avatar({ url, highlight, dim, size = 5 }) {
  const ringColor = highlight
    ? 'rgba(232,200,100,0.95)'
    : (dim ? 'rgba(120,110,100,0.4)' : `${GOLD} 0.55)`);
  const ringGlow = highlight
    ? `0 0 22px ${GOLD} 0.7), inset 0 0 8px rgba(255,235,180,0.45)`
    : '0 2px 8px rgba(0,0,0,0.5)';
  return (
    <div
      style={{
        width: `${size}cqw`,
        height: `${size}cqw`,
        borderRadius: '50%',
        border: `2px solid ${ringColor}`,
        boxShadow: ringGlow,
        overflow: 'hidden',
        background: 'rgba(0,0,0,0.5)',
        position: 'relative',
        filter: dim ? 'saturate(0.4) brightness(0.7)' : 'none',
        transition: 'border-color 300ms ease, box-shadow 300ms ease, filter 300ms ease',
      }}
    >
      {url ? (
        <AvatarImage src={url} className="w-full h-full" />
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: `${size * 0.32}cqw`,
            color: PARCHMENT + ' 0.4)',
          }}
        >
          ?
        </div>
      )}
    </div>
  );
}

// Pill-shaped chip displaying the rolled value beneath the die. Three
// styling tiers. Neutral (post-roll, undecided), gold (winner), muted
// (loser). Reads as a wax-sealed result tag pinned beneath the die.
function RollChip({ value, wins, dim }) {
  const accent = wins ? ACCENT_GOLD : (dim ? TEXT_MUTED : PARCHMENT + ' 0.85)');
  const borderColor = wins
    ? 'rgba(232, 200, 100, 0.95)'
    : (dim ? 'rgba(120, 110, 100, 0.35)' : `${GOLD} 0.55)`);
  const bg = wins
    ? 'linear-gradient(180deg, rgba(58, 40, 14, 0.95) 0%, rgba(38, 26, 10, 0.95) 100%)'
    : (dim
      ? 'linear-gradient(180deg, rgba(20, 18, 16, 0.85) 0%, rgba(12, 10, 8, 0.85) 100%)'
      : 'linear-gradient(180deg, rgba(28, 22, 14, 0.92) 0%, rgba(18, 14, 8, 0.92) 100%)');
  const glow = wins
    ? `0 0 16px ${GOLD} 0.55), inset 0 1px 0 rgba(255,235,180,0.18)`
    : 'inset 0 1px 0 rgba(255,235,180,0.05), 0 2px 6px rgba(0,0,0,0.5)';
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '4.5cqw',
        height: '2.4cqw',
        padding: '0 1.4cqw',
        background: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: 'var(--radius-full)',
        boxShadow: glow,
        animation: 'fpdRevealNumeral 380ms cubic-bezier(0.2, 0.9, 0.25, 1.05)',
        transition: 'border-color 300ms ease, box-shadow 300ms ease',
      }}
    >
      <span
        style={{
          fontFamily: 'Geist Variable, sans-serif',
          fontSize: '1.7cqw',
          fontWeight: 700,
          // The numeral sits above the line-box centre because the baseline
          // is below middle and the cap-height dominates the optical centre.
          // Nudging the line-height up makes the glyph occupy its own box
          // symmetrically inside the flex parent, so {value} renders
          // true-centred.
          lineHeight: '2.4cqw',
          display: 'block',
          color: accent,
          textShadow: wins
            ? `0 0 12px ${GOLD} 0.7), 0 1px 2px rgba(0,0,0,0.6)`
            : '0 1px 2px rgba(0,0,0,0.6)',
          transition: 'color 300ms ease',
          // Tiny optical-centre nudge: drop the glyph by a hair so
          // the visual mass (the digit body) sits on the chip's
          // centre line.
          transform: 'translateY(0.08cqw)',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function FooterPrompt({
  phase,
  remoteName, winnerName, firstPlayerName, localChooses, tiePending,
}) {
  let text = '';
  let italic = false;
  let medieval = false;
  if (phase === PHASE.CONFIRMED || phase === PHASE.EXITING) {
    text = firstPlayerName ? t('{name} plays first', { name: firstPlayerName }) : '';
  } else if (phase === PHASE.RESOLVED) {
    // The roll is done. Winner now chooses turn order. Tell each
    // side what's expected of them right now, not who won (the
    // halo + chip handle that).
    if (localChooses) {
      text = t('Who plays first?');
      medieval = true;
    } else {
      text = t('{name} won the roll', { name: winnerName || t('Opponent') });
    }
  } else if (tiePending) {
    text = t('Tie. Roll again');
    italic = true;
  }
  // IDLE prompts ("Click to roll" / "Rolling…" / "Waiting…") render
  // per-die in SeatPanel. No centered duplicate here.

  return (
    <div
      className={medieval ? 'arena-heading' : undefined}
      style={{
        textAlign: 'center',
        minHeight: '1.4cqw',
        fontSize: medieval ? '1.8cqw' : '1.1cqw',
        color: medieval ? ACCENT_GOLD : PARCHMENT + ' 0.85)',
        letterSpacing: medieval ? '0.02em' : '0.08em',
        fontStyle: italic ? 'italic' : 'normal',
        textShadow: '0 1px 3px rgba(0,0,0,0.7)',
        transition: 'color 300ms ease',
      }}
    >
      {text}
    </div>
  );
}

// ─── Keyframes (injected once via inline <style>) ──────────

const KEYFRAMES = `
@keyframes fpdRevealNumeral {
  0%   { opacity: 0; transform: translateY(0.4cqw) scale(0.6); filter: blur(2px); }
  60%  { opacity: 1; transform: translateY(0) scale(1.1); filter: blur(0); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes fpdEngravePulse {
  0%, 100% { opacity: 0.66; filter: drop-shadow(0 0 6px rgba(255, 168, 74, 0.2)); }
  50%      { opacity: 1; filter: drop-shadow(0 0 22px rgba(255, 168, 74, 0.55)) brightness(1.3); }
}
@keyframes fpdCritGlow {
  from { opacity: 0; }
  to   { opacity: 1; }
}
`;
